import { FileSharePermission } from '@kfolks/types'
import { z } from 'zod'

/**
 * Schema for requesting a file upload URL (HTTP POST /files/upload-url body).
 */
export const fileUploadRequestSchema = z.object({
	fileName: z.string().min(1),
	contentType: z.string().min(1),
	checksum: z.string().optional(),
	entityType: z.string().optional(),
	entityId: z.uuid().optional(),
	isPublic: z.boolean().default(false),
	description: z.string().optional(),
	idempotencyKey: z.uuid().optional(),
})

/**
 * Schema for updating file metadata (HTTP PATCH /files/{id} body).
 */
export const fileUpdateSchema = z.object({
	description: z.string().optional(),
	entityType: z.string().optional(),
	entityId: z.uuid().optional(),
})

/**
 * Schema for creating a file share (HTTP POST /files/{id}/shares body).
 */
export const fileShareCreateSchema = z.object({
	userId: z.uuid(),
	permission: z.enum(FileSharePermission),
	expiresAt: z.coerce.date().optional(),
})

/**
 * Allow-list of `entityType` values accepted by the rebind endpoint.
 *
 * The rebind contract intentionally restricts the universe of valid targets
 * so a caller can't reassign files to arbitrary strings. Add new entries
 * here as new entity surfaces gain attachment support.
 */
export const REBINDABLE_ENTITY_TYPES = ['request_center_request', 'ticket'] as const
export type RebindableEntityType = (typeof REBINDABLE_ENTITY_TYPES)[number]

/**
 * Schema for the rebind endpoint (HTTP PATCH /files/rebind body).
 *
 * Rebinding moves a batch of files from a placeholder/legacy binding to a
 * real entity. Per-file ownership (uploader OR ADMIN/SUPER_ADMIN) is
 * enforced at the service layer — the schema only validates shape.
 */
export const fileRebindRequestSchema = z.object({
	fileIds: z.array(z.uuid()).min(1).max(100),
	target: z.object({
		entityType: z.enum(REBINDABLE_ENTITY_TYPES),
		entityId: z.uuid(),
	}),
})

export type FileUploadRequestInput = z.infer<typeof fileUploadRequestSchema>
export type FileUpdateInput = z.infer<typeof fileUpdateSchema>
export type FileShareCreateInput = z.infer<typeof fileShareCreateSchema>
export type FileRebindRequestInput = z.infer<typeof fileRebindRequestSchema>
